import classNames from 'classnames';
import _ from 'lodash';
import React, { memo, useMemo, useRef } from 'react';
import { Whisper } from 'rsuite';
import { GerenciadorPrecos } from '../../../../../../@types/GerenciadorPrecos';
import { CurrencyInput, InputAddon, InputGroup } from '../../../../../../components';
import { CurrencyInputProps } from '../../../../../../components/CurrencyInput/CurrencyInput.types';
import { isBrokenedPriceList, isChangedPrice, IPAMaths as m } from '../../../../../../lib';
import { useEditedPriceManagerStore } from '../../../../../../store/priceManagerStore';
import { VariationBoxIndicator } from '../../../../Negociacoes/pages/NegociacaoFornecedor/components';
import PriceStatusIndicator from '../../PriceStatusIndicator/PriceStatusIndicator';
import { QuebraLimitesInfo } from '../../QuebraLimitesInfo';
import { shouldHighlightBrokenLimit } from '../../../utils/brokenLimitUtils';
import BaseCell, { BaseCellWithUpdateCampoProps } from '../BaseCell/BaseCell';

type RowData = GerenciadorPrecos.RowData;

export type PrecoSugeridoCellProps = BaseCellWithUpdateCampoProps;

const getSkin = (rowData: RowData): CurrencyInputProps['skin'] => {
  if (shouldHighlightBrokenLimit({ rowData, localBroken: isBrokenedPriceList(rowData), type: 'PRICE_LIST' })) return 'red';
  if (isChangedPrice(rowData)) return 'blue';
  return 'gray';
};

const handleEnterKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, className: string) => {
  if (e.key === 'Enter') {
    e.preventDefault();

    const currentInput = e.currentTarget;
    const nodeList = document.querySelectorAll(`.${className}`);
    const currentIndex = Array.from(nodeList).indexOf(currentInput);
    const nextIndex = currentIndex + 1;

    const nextInput = nextIndex < nodeList.length ? (nodeList[nextIndex] as HTMLInputElement) : null;

    currentInput.blur();

    if (nextInput) {
      setTimeout(() => {
        nextInput.focus();
        setTimeout(() => {
          nextInput.select();
        }, 100);
      }, 50);
    } else {
      console.log('⚠️ No next input found (last row)');
    }
  }
};

const PrecoSugeridoCell = ({
  className,
  dataKey,
  rowData,
  index,
  onBlur,
  handleBlur,
  handleContextMenu,
  handleUpdateCampo,
  ...props
}: PrecoSugeridoCellProps) => {
  const { priceStatus, price, retailPrice } = rowData;
  const isPendingEditedPrice = useEditedPriceManagerStore(state => Boolean(state.pendingEditedPriceRows[rowData.productsToBePricedId]));
  const hasEditedSinceFocusRef = useRef(false);

  const priceVariation = useMemo(() => {
    return m.variation.price(price, retailPrice);
  }, [price, retailPrice]);

  return (
    <BaseCell handleContextMenu={handleContextMenu} rowData={rowData} className={classNames(className)}>
      <BaseCell.MainContainer>
        <InputGroup>
          <Whisper
            trigger="click"
            placement="bottomEnd"
            // @ts-ignore
            speaker={<QuebraLimitesInfo content={rowData} />}
          >
            <InputAddon>R$</InputAddon>
          </Whisper>
          <CurrencyInput
            name={dataKey}
            className="novo-preco"
            decimalSeparator=","
            thousandSeparator="."
            precision={2}
            allowEmpty
            allowNegative={false}
            value={price || 0}
            onFocus={() => {
              hasEditedSinceFocusRef.current = false;
            }}
            onChangeEvent={(_e, _m, v) => {
              hasEditedSinceFocusRef.current = true;
              handleUpdateCampo(dataKey, v, rowData, index);
            }}
            skin={getSkin(rowData)}
            onBlur={e => {
              onBlur?.(e);

              const wasEdited = hasEditedSinceFocusRef.current;
              hasEditedSinceFocusRef.current = false;

              if (wasEdited) handleBlur(rowData, index);
            }}
            onKeyDown={e => handleEnterKeyPress(e, 'novo-preco')}
            selectOnFocus
            {..._.omit(props, ['value', 'disabled'])}
            disabled={isPendingEditedPrice}
          />
        </InputGroup>
      </BaseCell.MainContainer>
      <BaseCell.SubContainer>
        {!!price && !!retailPrice ? <VariationBoxIndicator value={priceVariation} suffix="%" /> : <>--</>}
        <PriceStatusIndicator priceStatus={priceStatus} />
      </BaseCell.SubContainer>
    </BaseCell>
  );
};

export default memo(PrecoSugeridoCell);
